/* eslint-disable react/jsx-closing-tag-location */
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    AccordionActions,
    Divider,
    Avatar,
    Typography,
} from '@material-ui/core';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    applyUkgUserTimeZoneTransformation, convertUtcDateToDateInUserTimezone,
    // eslint-disable-next-line import/no-unresolved
} from '@commonClient2/services/ukgDateTime';
import { UPDATE_DRAFT_BID_PROFILE_SCHEDULE, applyScheduleDraft, getSequentialEmployeeScheduleDraft } from '../../store/bidProfileDraft';
import { UPDATE_BID_PROFILE_SCHEDULE, applySchedule, getSequentialEmployeeSchedule } from '../../store/bidProfile';
import { BidProcessType, EmployeeSubmissionTimeUnit, BidProfileStatus } from '../../../Enums';
import {
    Fields, roundDatePickerValues, generateDateRangeData, isDateRangeInvalid, isValidDate,
} from './SchedulePeriod.service';
import SchedulePeriodPreferential from './SchedulePeriod-Preferential';
import SchedulePeriodSequential from './SchedulePeriod-Sequential';
import './SchedulePeriod.scss';

const SchedulePeriod = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');
    const now = [applyUkgUserTimeZoneTransformation(new Date()), applyUkgUserTimeZoneTransformation(new Date())];

    /** @type {BiddingDraft.IBidProfileDraft} */
    // @ts-ignore
    const { managerAppData, bidProfileDraft, bidProfile } = useSelector((state) => state);
    const {
        bidProfileDrafts, bidProfiles,
    } = managerAppData;

    const [validationErrors, setValidationErrors] = useState({
        submissionPeriod: false,
        requestPeriod: false,
        submissionBeforeRequest: false,
        timePerEmployeeValue: false,
        dayOfTheWeek: false,
        biddingTimeSmallerThanTimePerEmployee: false,
    });

    const [isEstimatingEndDate, setIsEstimatingEndDate] = useState(false);

    /**
     * @param {string} field
     * @param {Date[]} value
     * @param {string | Date | null} requestPeriodStartDate
     * @param {string | Date | null} submissionPeriodEndDate
     */
    const removeValidationError = (field, value, requestPeriodStartDate, submissionPeriodEndDate) => {
        const [startDate, endDate] = value;
        switch (field) {
            case Fields.SubmissionPeriod:
                if (validationErrors.submissionPeriod || validationErrors.submissionBeforeRequest) {
                    if (!isDateRangeInvalid(startDate, endDate)) {
                        const isBeforeError = endDate > requestPeriodStartDate;
                        const errors = { ...validationErrors, submissionPeriod: false, submissionBeforeRequest: isBeforeError };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.RequestPeriod:
                if (validationErrors.requestPeriod || validationErrors.submissionBeforeRequest) {
                    if (!isDateRangeInvalid(startDate, endDate)) {
                        const isBeforeError = submissionPeriodEndDate > startDate;
                        const errors = { ...validationErrors, requestPeriod: false, submissionBeforeRequest: isBeforeError };
                        setValidationErrors(errors);
                    }
                }
                break;
            default:
                break;
        }
    };

    /**
     * @param {BidProfile.IBidProfileScheduling} schedule
     * @param {any} errors
     */
    const sequentialScheduleValuesHasError = (schedule, errors = {}) => {
        const {
            submissionPeriodStartDate,
            timePerEmployeeValue,
            timePerEmployeeUnit,
            startBiddingTime,
            endBiddingTime,
            dayOfTheWeek,
            isAnytime,
        } = schedule;
        let hasError = false;
        const isSubmissionPeriodInvalid = !submissionPeriodStartDate;
        if (isSubmissionPeriodInvalid) {
            hasError = true;
            errors.submissionPeriod = true;
        }
        if (timePerEmployeeValue < 1) {
            hasError = true;
            errors.timePerEmployeeValue = true;
        }
        if (!isAnytime) {
            const startBiddingTimeAsMoment = moment(startBiddingTime);
            const endBiddingTimeAsMoment = moment(endBiddingTime);
            if (endBiddingTime < startBiddingTime) {
                endBiddingTimeAsMoment.add(1, 'days');
            }
            const minutesPerEmployee = timePerEmployeeUnit === EmployeeSubmissionTimeUnit.Hour
                ? (timePerEmployeeValue * 60) : timePerEmployeeValue;
            const minutesPerDay = endBiddingTimeAsMoment.diff(startBiddingTimeAsMoment, 'minutes');
            if (minutesPerDay < minutesPerEmployee) {
                hasError = true;
                errors.biddingTimeSmallerThanTimePerEmployee = true;
            }
        }
        if (!Object.values(dayOfTheWeek).some((x) => x === true)) {
            hasError = true;
            errors.dayOfTheWeek = true;
        }
        return hasError;
    };

    /**
     * @param {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile} profile
     * @returns {boolean}
     */
    const validate = (profile) => {
        let hasError = false;
        const errors = {
            submissionPeriod: false,
            requestPeriod: false,
            submissionBeforeRequest: false,
            timePerEmployeeValue: false,
            dayOfTheWeek: false,
            biddingTimeSmallerThanTimePerEmployee: false,
        };
        const {
            submissionPeriodStartDate,
            submissionPeriodEndDate,
            requestPeriodStartDate,
            requestPeriodEndDate,
        } = profile.schedule;
        const { bidProcessType } = profile.originalBidProfileInformation;
        if (bidProcessType === BidProcessType.Preferential) {
            const isSubmissionPeriodInvalid = isDateRangeInvalid(submissionPeriodStartDate, submissionPeriodEndDate);
            if (isSubmissionPeriodInvalid) {
                hasError = true;
                errors.submissionPeriod = true;
            }

            const isRequestPeriodInvalid = isDateRangeInvalid(requestPeriodStartDate, requestPeriodEndDate);
            if (isRequestPeriodInvalid) {
                hasError = true;
                errors.requestPeriod = true;
            }
            if (!isSubmissionPeriodInvalid && !isRequestPeriodInvalid) {
                // @ts-ignore
                if (submissionPeriodEndDate > requestPeriodStartDate) {
                    hasError = true;
                    errors.submissionBeforeRequest = true;
                }
            }
        } else if (bidProcessType === BidProcessType.Sequential) {
            hasError = sequentialScheduleValuesHasError(profile.schedule, errors);

            const isRequestPeriodInvalid = isDateRangeInvalid(requestPeriodStartDate, requestPeriodEndDate);
            if (isRequestPeriodInvalid) {
                hasError = true;
                errors.requestPeriod = true;
            }
            const isSubmissionPeriodInvalid = !submissionPeriodStartDate;
            if (!isSubmissionPeriodInvalid && !isRequestPeriodInvalid) {
                // @ts-ignore
                if (submissionPeriodEndDate >= requestPeriodStartDate) {
                    hasError = true;
                    errors.submissionBeforeRequest = true;
                }
            }
        }

        if (errors.submissionPeriod !== validationErrors.submissionPeriod
            || errors.requestPeriod !== validationErrors.requestPeriod
            || errors.submissionBeforeRequest !== validationErrors.submissionBeforeRequest
            || errors.timePerEmployeeValue !== validationErrors.timePerEmployeeValue
            || errors.dayOfTheWeek !== validationErrors.dayOfTheWeek
            || errors.biddingTimeSmallerThanTimePerEmployee !== validationErrors.biddingTimeSmallerThanTimePerEmployee
        ) {
            setValidationErrors(errors);
        }

        return !hasError;
    };

    /**
     * @param {boolean} expanded
     */
    // @ts-ignore
    // eslint-disable-next-line no-unused-vars
    let toggleAccordion = (expanded) => { };
    let handleConfirm = () => { };
    let handleDiscard = () => { };
    /**
     * @param {string} field
     * @param {moment.Moment[] | Date[]} updatedValue
     */
    let handleDateRangeChange = (field, updatedValue) => { };

    /**
     * @param {string} target
     * @param {Date | boolean | BidProfile.IDayOfWeekDictionary | EmployeeSubmissionTimeUnit} value
     */
    let updateSchedulePeriodStore = (target, value) => { };
    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile}
     */
    let profile = {};
    /**
     * @param {BidProfile.IBidProfileSchedulingAccordion} schedule
     */
    let handleRecalculateEmployeeSchedule = (schedule) => { };
    const debouncedUpdate = useCallback(
        _.debounce((x) => handleRecalculateEmployeeSchedule(x), 1000),
        [JSON.stringify(bidProfileDraft.originalEmployees), JSON.stringify(bidProfile.originalEmployees), JSON.stringify(bidProfileDraft.employees), JSON.stringify(bidProfile.employees)],
    );

    /**
     * @type {BidProcessType | undefined}
     */
    let bidProcessType;
    let isActivePrefentialBid = false;

    let [requestPeriodMinDate] = now;
    /**
     * @type {Date | null}
     */
    let requestPeriodMaxDate = null;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
        bidProcessType = profile.originalBidProfileInformation.bidProcessType;
        const {
            submissionPeriodEndDate,
            requestPeriodStartDate,
        } = profile.schedule;

        requestPeriodMinDate = isValidDate(submissionPeriodEndDate) || now[0];

        handleRecalculateEmployeeSchedule = (schedule) => {
            if (!sequentialScheduleValuesHasError(schedule)) {
                setIsEstimatingEndDate(true);
                try {
                    const currentEmployees = profile.employees && profile.employees.employeesIds && profile.employees.employeesIds.length > 0
                        ? profile.employees 
                        : profile.originalEmployees;

                    dispatch(
                        getSequentialEmployeeScheduleDraft(bidProfileDraft.draftId, { employees: currentEmployees, schedule }),
                    );
                } finally {
                    setIsEstimatingEndDate(false);
                }
            }
        };

        toggleAccordion = (expanded) => {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                payload: { isExpanded: expanded },
            });
        };

        handleConfirm = () => {
            if (validate(bidProfileDraft)) {
                dispatch(applyScheduleDraft(bidProfileDraft.draftId, { bidProfileDrafts, bidProfileDraft }));
            }
        };

        handleDiscard = () => {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                payload: _.cloneDeep({ ...bidProfileDraft.originalSchedule, isExpanded: true }),
            });
        };

        handleDateRangeChange = (field, updatedValue) => {
            const [startDate, endDate] = updatedValue;
            const values = !(updatedValue[0] instanceof Date)
                // @ts-ignore
                ? [updatedValue[0].toDate(), updatedValue[1].toDate()]
                : updatedValue;

            let changesRequestPeriod = {};

            const isSubmissionBeyond = (
                values[1] && values[1] > requestPeriodStartDate
            );

            if (field === Fields.SubmissionPeriod) {
                if (isSubmissionBeyond && !bidProfileDraft.originalScheduleTemplates.isActiveWorkplanBid) {
                    // Only update requestPeriodStartDate, preserve the existing requestPeriodEndDate
                    changesRequestPeriod = {
                        requestPeriodStartDate: values[1],
                    };
                }
            }

            /**
             * @type {Date[]}
             */
            let datePicker;
            if (!(startDate instanceof Date)) {
                // @ts-ignore
                datePicker = [startDate.toDate(), endDate.toDate()];
            } else {
                // @ts-ignore
                datePicker = updatedValue;
            }
            const roundedValues = roundDatePickerValues(field, datePicker);
            removeValidationError(field, roundedValues, requestPeriodStartDate, submissionPeriodEndDate);
            let changes;
            if (bidProcessType === BidProcessType.Sequential && field === Fields.SubmissionPeriod) {
                const [roundedStartDate] = roundedValues;
                changes = {
                    submissionPeriodStartDate: roundedStartDate,
                    submissionPeriodEndDate: roundedStartDate,
                };
                debouncedUpdate({ ...profile.schedule, submissionPeriodStartDate: roundedStartDate });
            } else {
                changes = generateDateRangeData(field, roundedValues);
            }
            if (changes) {
                const action = {
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload: {
                        ...changes,
                        ...changesRequestPeriod,
                    },
                };
                dispatch(action);
            }
        };

        updateSchedulePeriodStore = (target, value) => {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                payload: {
                    [target]: value,
                },
            });

            debouncedUpdate({ ...profile.schedule, [target]: value });
        };

        if (bidProfileDraft.isRebid) {
            const parentReqStartDate = bidProfileDraft.parentRequestPeriodStartDate;
            requestPeriodMinDate = parentReqStartDate > requestPeriodMinDate ? parentReqStartDate : requestPeriodMinDate;
            requestPeriodMaxDate = bidProfileDraft.originalRequestPeriodEndDate;
        } else if (bidProfileDraft.originalScheduleTemplates.isActiveWorkplanBid) {
            const workplanStartDate = convertUtcDateToDateInUserTimezone(
                bidProfileDraft.originalScheduleTemplates.activeWorkplanStartDate * 1000,
            );
            requestPeriodMinDate = workplanStartDate > requestPeriodMinDate ? workplanStartDate : requestPeriodMinDate;
            requestPeriodMaxDate = bidProfileDraft.originalSchedule.requestPeriodEndDate;
        }
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
        bidProcessType = profile.originalBidProfileInformation.bidProcessType;
        isActivePrefentialBid = bidProfile.status === BidProfileStatus.Active && bidProcessType === BidProcessType.Preferential;
        const {
            submissionPeriodEndDate,
            requestPeriodStartDate,
        } = profile.schedule;

        requestPeriodMinDate = isValidDate(submissionPeriodEndDate) || now[0];
        if (bidProfile.isRebid) {
            requestPeriodMaxDate = bidProfile.originalRequestPeriodEndDate;
        } else if (bidProfile.originalScheduleTemplates.isActiveWorkplanBid) {
            const workplanStartDate = convertUtcDateToDateInUserTimezone(
                bidProfile.originalScheduleTemplates.activeWorkplanStartDate * 1000,
            );
            requestPeriodMinDate = workplanStartDate > requestPeriodMinDate ? workplanStartDate : requestPeriodMinDate;
            requestPeriodMaxDate = bidProfile.originalSchedule.requestPeriodEndDate;
        }
        handleRecalculateEmployeeSchedule = (schedule) => {
            // @ts-ignore
            if (!sequentialScheduleValuesHasError(schedule, {})) {
                setIsEstimatingEndDate(true);
                try {
                    const currentEmployees = profile.employees && profile.employees.employeesIds && profile.employees.employeesIds.length > 0
                        ? profile.employees 
                        : profile.originalEmployees;
                    dispatch(getSequentialEmployeeSchedule(bidProfile.profileId, { employees: currentEmployees, schedule }));
                } finally {
                    setIsEstimatingEndDate(false);
                }
            }
        };

        toggleAccordion = (expanded) => {
            dispatch({
                type: UPDATE_BID_PROFILE_SCHEDULE,
                payload: { isExpanded: expanded },
            });
        };

        handleConfirm = () => {
            if (validate(bidProfile)) {
                dispatch(applySchedule(bidProfile.profileId, { bidProfiles, bidProfile }));
            }
        };

        handleDiscard = () => {
            dispatch({
                type: UPDATE_BID_PROFILE_SCHEDULE,
                payload: _.cloneDeep({ ...bidProfile.originalSchedule, isExpanded: true }),
            });
        };

        handleDateRangeChange = (field, updatedValue) => {
            const [startDate, endDate] = updatedValue;
            const values = !(updatedValue[0] instanceof Date) || !(updatedValue[1] instanceof Date)
                // @ts-ignore
                ? [updatedValue[0].toDate(), updatedValue[1].toDate()]
                : updatedValue;

            let changesRequestPeriod = {};

            const isSubmissionBeyond = (
                updatedValue[1] && updatedValue[1] > requestPeriodStartDate
            );

            if (field === Fields.SubmissionPeriod) {
                if (isSubmissionBeyond && !bidProfile.originalScheduleTemplates.isActiveWorkplanBid) {
                    // Only update requestPeriodStartDate, preserve the existing requestPeriodEndDate
                    changesRequestPeriod = {
                        requestPeriodStartDate: values[1],
                    };
                }
            }

            let datePicker;
            if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
                // @ts-ignore
                datePicker = [startDate.toDate(), endDate.toDate()];
            } else {
                datePicker = updatedValue;
            }
            
            const roundedValues = roundDatePickerValues(field, datePicker);
            removeValidationError(field, roundedValues, requestPeriodStartDate, submissionPeriodEndDate);
            let changes;
            if (bidProcessType === BidProcessType.Sequential && field === Fields.SubmissionPeriod) {
                const [roundedStartDate] = roundedValues;
                changes = {
                    submissionPeriodStartDate: roundedStartDate,
                    submissionPeriodEndDate: roundedStartDate,
                };

                debouncedUpdate({ ...profile.schedule, submissionPeriodStartDate: roundedStartDate });
            } else if (isActivePrefentialBid && field === Fields.SubmissionPeriod) {
                const [_, roundedEndDate] = roundedValues;
                changes = {
                    submissionPeriodEndDate: roundedEndDate,
                };
            } else {
                changes = generateDateRangeData(field, roundedValues);
            }
            if (changes) {
                const action = {
                    type: UPDATE_BID_PROFILE_SCHEDULE,
                    payload: {
                        ...bidProfile.schedule,
                        ...changes,
                        ...changesRequestPeriod,
                    },
                };
                dispatch(action);
            }
        };

        updateSchedulePeriodStore = (target, value) => {
            dispatch({
                type: UPDATE_BID_PROFILE_SCHEDULE,
                payload: {
                    [target]: value,
                },
            });
            debouncedUpdate({ ...profile.schedule, [target]: value });
        };
    } else {
        // Ensure bidProcessType is always assigned to avoid "used before assigned"
        bidProcessType = undefined;
    }

    const {
        schedule,
        isCreationAllowed,
        isViewAllowed,
    } = profile;

    const {
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        isEditable,
        isExpanded
    } = schedule;

    const requestPeriod = [
        requestPeriodMinDate,
        requestPeriodMaxDate || applyUkgUserTimeZoneTransformation(new Date()),
    ];

    const defaultSubmissionPeriodValues = roundDatePickerValues(Fields.SubmissionPeriod, now);
    const defaultRequestPeriodValues = roundDatePickerValues(Fields.RequestPeriod, requestPeriod);

    const setPeriodsToDefaults = () => {
        if (profile) {
            if (!submissionPeriodStartDate && !submissionPeriodEndDate) {
                handleDateRangeChange(Fields.SubmissionPeriod, defaultSubmissionPeriodValues);
            }
            if (!requestPeriodStartDate && !requestPeriodEndDate) {
                handleDateRangeChange(Fields.RequestPeriod, defaultRequestPeriodValues);
            }
        }
    };

    useEffect(setPeriodsToDefaults, []);

    useEffect(() => {
        if (profile && schedule && schedule.isExpanded && bidProcessType === BidProcessType.Sequential) {
            if (!sequentialScheduleValuesHasError(schedule)) {
                handleRecalculateEmployeeSchedule(schedule);
            }
        }
    }, [
        JSON.stringify(bidProfileDraft.employees?.employeesIds || []), 
        JSON.stringify(bidProfile.employees?.employeesIds || []),
        JSON.stringify(bidProfileDraft.originalEmployees?.employeesIds || []), 
        JSON.stringify(bidProfile.originalEmployees?.employeesIds || []),
        profile?.schedule?.isExpanded
    ]);

    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }

    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isInputDisabled = !isEditable || hasOnlyViewAccess;

    return (
        <Accordion
            expanded={isExpanded}
            // @ts-ignore
            onChange={(event, expanded) => toggleAccordion(expanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Avatar className="bde-avatar">{4}</Avatar>
                <Typography variant="h6">{t('form.schedulePeriod.header')}</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
                {bidProcessType === BidProcessType.Preferential && (
                    <SchedulePeriodPreferential
                        profile={profile}
                        requestPeriod={requestPeriod}
                        requestPeriodMinDate={requestPeriodMinDate}
                        requestPeriodMaxDate={requestPeriodMaxDate}
                        validationErrors={validationErrors}
                        handleDateRangeChange={handleDateRangeChange}
                    />
                )}
                {bidProcessType === BidProcessType.Sequential && (
                    <SchedulePeriodSequential
                        profile={profile}
                        requestPeriod={requestPeriod}
                        requestPeriodMinDate={requestPeriodMinDate}
                        requestPeriodMaxDate={requestPeriodMaxDate}
                        isEstimatingEndDate={isEstimatingEndDate}
                        validationErrors={validationErrors}
                        handleDateRangeChange={handleDateRangeChange}
                        updateSchedulePeriodStore={updateSchedulePeriodStore}
                    />
                )}
                {(bidProcessType !== BidProcessType.Preferential && bidProcessType !== BidProcessType.Sequential) && (
                    <>{t('form.schedulePeriod.noBidConfigurationSet')}</>
                )}
            </AccordionDetails>
            {!hasOnlyViewAccess && (
                <>
                    <Divider />
                    <AccordionActions>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleDiscard}
                            disabled={!isActivePrefentialBid && isInputDisabled}
                        >
                            {t('globalForm.discard')}
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleConfirm}
                            // eslint-disable-next-line max-len
                            disabled={(!isActivePrefentialBid && isInputDisabled) || isEstimatingEndDate}
                            className="save-continue--button"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            {t('globalForm.applyButton')}
                        </Button>
                    </AccordionActions>
                </>
            )}
        </Accordion>
    );
};

export default SchedulePeriod;
